<template>
  <div class="product-kits-page">
    <!-- 1. Banner Section -->
    <div class="banner-section">
      <div class="banner-content">
        <div class="banner-text">
          <h1 class="banner-title">{{ bannerTitle }}</h1>
          <p class="banner-desc">
            {{ bannerDesc }}
          </p>
        </div>
        <div class="banner-image-wrapper">
          <img src="@/assets/product/kits/banner.png"
               :alt="bannerTitle"
               class="banner-img" />
        </div>
      </div>
    </div>

    <!-- 2. Education Kits Section -->
    <div class="kits-section">
      <div class="section-container">
        <div class="section-header">
          <h2 class="section-title">{{ sections.educationTitle }}</h2>
          <p class="section-desc">{{ sections.educationDesc }}</p>
        </div>

        <!-- Tabs -->
        <div class="kits-tabs">
          <div class="tab-item"
               v-for="(tab, index) in kitsTabs"
               :key="index"
               @click="currentKitIndex = index"
               :class="{ active: currentKitIndex === index }"
               :style="currentKitIndex === index ? { backgroundColor: tab.activeColor, color: '#fff' } : {}">
            {{ tab.name }}
          </div>
        </div>

        <!-- Content Area -->
        <div class="kit-content-card"
             v-if="currentKit">
          <div class="kit-text-area">
            <h3 class="kit-title">{{ currentKit.title }}</h3>
            <p class="kit-desc">{{ currentKit.desc }}</p>
          </div>
          <div class="kit-image-area">
            <img :src="currentKit.image"
                 :alt="currentKit.title"
                 class="kit-img" />
          </div>
        </div>
      </div>
    </div>

    <!-- 3. Creativity Section -->
    <div class="creativity-section">
      <div class="section-container">
        <div class="section-header">
          <h2 class="section-title">{{ sections.creativityTitle }}</h2>
        </div>

        <div class="creativity-list">
          <div class="creativity-item"
               v-for="(item, index) in creativityItems"
               :key="index">
            <div class="creativity-img-wrapper">
              <img :src="item.image"
                   :alt="item.title"
                   class="creativity-img" />
            </div>
            <h3 class="creativity-title">{{ item.title }}</h3>
            <p class="creativity-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 4. Diverse Education Projects Section -->
    <div class="projects-section">
      <div class="section-container">
        <div class="section-header">
          <h2 class="section-title">{{ sections.projectsTitle }}</h2>
        </div>

        <div class="projects-grid">
          <div class="project-item"
               v-for="(item, index) in projectItems"
               :key="index">
            <div class="project-img-wrapper">
              <img :src="item.image"
                   :alt="item.title"
                   class="project-img">
            </div>
            <div class="project-title-bar"
                 :style="{ backgroundColor: item.color }">
              {{ item.title }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 5. Bottom Banner Section -->
    <div class="bottom-banner-section">
      <div class="bottom-banner-container">
        <div class="bottom-banner-text">
          <p>
            {{ bottomBannerText }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductKits',
  data() {
    return {
      currentKitIndex: 0,
      kitAssets: [
        {
          activeColor: '#4285F4',
          image:
            require('@/assets/product/kits/edu1.png'),
        },
        {
          activeColor: '#92D300',
          image:
            require('@/assets/product/kits/edu2.png'),
        },
        {
          activeColor: '#FBBC05',
          image:
            require('@/assets/product/kits/edu3.png'),
        },
      ],
      creativityAssets: [
        {
          image:
            require('@/assets/product/kits/create1.png'),
        },
        {
          image:
            require('@/assets/product/kits/create2.png'),
        },
        {
          image:
            require('@/assets/product/kits/create3.png'),
        },
      ],
      projectAssets: [
        {
          color: '#4285F4',
          image:
            require('@/assets/product/kits/project1.png'),
        },
        {
          color: '#97C908',
          image:
            require('@/assets/product/kits/project2.png'),
        },
        {
          color: '#FBBC05',
          image:
            require('@/assets/product/kits/project3.png'),
        },
        {
          color: '#EA4335',
          image:
            require('@/assets/product/kits/project4.png'),
        },
      ],
    }
  },
  computed: {
    productKitsCopy() {
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'zh-CN'
      const messages =
        this.$i18n && this.$i18n.getLocaleMessage
          ? this.$i18n.getLocaleMessage(locale)
          : {}
      return (messages.product && messages.product.kits) || {}
    },
    bannerTitle() {
      return this.productKitsCopy.bannerTitle || ''
    },
    bannerDesc() {
      return this.productKitsCopy.bannerDesc || ''
    },
    sections() {
      return this.productKitsCopy.sections || {}
    },
    kitsTabs() {
      const tabs = this.productKitsCopy.kitsTabs || []
      return tabs.map((item, index) => {
        const asset = this.kitAssets[index] || {}
        return {
          name: item.name || '',
          activeColor: asset.activeColor || '#4285F4',
          data: {
            title: item.title || '',
            desc: item.desc || '',
            image: asset.image || '',
          },
        }
      })
    },
    creativityItems() {
      const items = this.productKitsCopy.creativityItems || []
      return items.map((item, index) => {
        const asset = this.creativityAssets[index] || {}
        return {
          title: item.title || '',
          desc: item.desc || '',
          image: asset.image || '',
        }
      })
    },
    projectItems() {
      const items = this.productKitsCopy.projectItems || []
      return items.map((item, index) => {
        const asset = this.projectAssets[index] || {}
        return {
          title: item.title || '',
          color: asset.color || '#4285F4',
          image: asset.image || '',
        }
      })
    },
    bottomBannerText() {
      return this.productKitsCopy.bottomBannerText || ''
    },
    currentKit() {
      const current = this.kitsTabs[this.currentKitIndex]
      return current ? current.data : { title: '', desc: '', image: '' }
    }
  }
}
</script>

<style scoped>
.product-kits-page {
  width: 100%;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
}

/* 1. Banner Section */
.banner-section {
  width: 100%;
  height: 680px;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.banner-content {
  width: 100%;
  /* Max width logic to keep text aligned but image extending */
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
}

.banner-text {
  width: 50%;
  padding-left: calc((100% - 1200px) / 2); /* Align to 1200px container grid */
  padding-right: 50px;
  box-sizing: border-box;
}

.banner-title {
  font-size: 48px;
  color: #333;
  font-weight: bold;
  margin-bottom: 30px;
}

.banner-desc {
  font-size: 24px;
  color: #666;
  line-height: 1.6;
}

.banner-image-wrapper {
  width: 50%;
  height: 680px;
  display: flex;
  justify-content: flex-end; /* Align image to right edge */
}

.banner-img {
  width: 1083px; /* Fixed width as requested */
  height: 100%;
  object-fit: contain;
  /* Mask effect simulation if needed, for now just standard image */
}

@media (max-width: 1900px) {
  .banner-section {
    height: 560px;
  }

  .banner-text {
    padding-left: 60px;
    padding-right: 40px;
  }

  .banner-title {
    font-size: 40px;
    margin-bottom: 20px;
  }

  .banner-desc {
    font-size: 20px;
  }

  .banner-image-wrapper {
    height: 100%;
  }

  .banner-img {
    width: 895px;
  }
}

@media (max-width: 1366px) {
  .banner-section {
    height: 520px;
  }

  .banner-content {
    padding: 0 40px;
  }

  .banner-text {
    width: 52%;
    padding-left: 0;
    padding-right: 24px;
  }

  .banner-title {
    font-size: 36px;
    margin-bottom: 16px;
  }

  .banner-desc {
    font-size: 18px;
  }

  .banner-image-wrapper {
    width: 48%;
    height: 520px;
    justify-content: center;
  }

  .banner-img {
    width: 100%;
    max-width: 640px;
  }
}

@media (max-width: 1200px) {
  .banner-section {
    height: auto;
    padding: 60px 0;
  }

  .banner-content {
    flex-direction: column;
    gap: 30px;
  }

  .banner-text {
    width: 100%;
    padding: 0 32px;
  }

  .banner-title {
    font-size: 34px;
  }

  .banner-desc {
    font-size: 18px;
  }

  .banner-image-wrapper {
    width: 100%;
    height: 360px;
    justify-content: center;
  }

  .banner-img {
    width: 90%;
    max-width: 720px;
    height: 100%;
  }
}

/* 2. Education Kits Section */
.kits-section {
  width: 100%;
  height: 1119px;
  background-color: #f2f2f2;
  padding-top: 80px;
}

.section-container {
  width: 1200px;
  margin: 0 auto;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: 34px;
  color: #333;
  font-weight: bold;
  margin-bottom: 20px;
}

.section-desc {
  font-size: 18px;
  color: #666;
}

/* Tabs */
.kits-tabs {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-bottom: 60px;
}

.tab-item {
  font-size: 20px;
  color: #333;
  cursor: pointer;
  padding: 0 40px;
  height: 63px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 94px;
  transition: all 0.3s ease;
  min-width: 200px; /* Minimum width for better look */
}

.tab-item:hover {
  background-color: #e0e0e0; /* Hover state for non-active */
}

.tab-item.active {
  color: #fff;
  /* background-color is handled by inline style */
}

/* Content Card */
.kit-content-card {
  width: 1200px;
  height: 662px;
  background-color: #fff;
  border-radius: 85px;
  display: flex;
  overflow: hidden;
}

.kit-text-area {
  width: 514px;
  height: 100%;
  padding: 100px 60px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.kit-title {
  font-size: 18px; /* Requested 18px, but usually title is larger. Sticking to user req. */
  font-weight: bold;
  color: #333;
  margin-bottom: 30px;
  white-space: pre-line; /* Handle newline characters */
  font-size: 24px; /* Slight adjustment for better hierarchy, user said 18px but it might be too small for title inside card */
}

.kit-desc {
  font-size: 18px;
  color: #666;
  line-height: 1.8;
}

.kit-image-area {
  width: 686px;
  height: 100%;
}

.kit-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 3. Creativity Section */
.creativity-section {
  width: 100%;
  height: 1023px;
  background-color: #f4f6fa;
  padding-top: 100px;
}

.creativity-list {
  display: flex;
  justify-content: space-between;
  margin-top: 80px;
}

.creativity-item {
  width: 381px;
  text-align: center;
}

.creativity-img-wrapper {
  width: 381px;
  height: 469px;
  border-radius: 30px;
  overflow: hidden;
  margin-bottom: 40px;
}

.creativity-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.creativity-item:hover .creativity-img {
  transform: scale(1.05);
}

.creativity-title {
  font-size: 18px; /* Requested 18px */
  color: #333;
  font-weight: bold;
  margin-bottom: 20px;
}

.creativity-desc {
  font-size: 18px; /* Requested 18px */
  color: #666;
  line-height: 1.6;
  padding: 0 20px;
}

/* 4. Projects Section */
.projects-section {
  width: 100%;
  height: 1336px;
  background-color: #f2f2f2;
  padding-top: 80px;
}

.projects-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px; /* Adjust gap as needed */
  justify-items: center;
  margin-top: 60px;
}

.project-item {
  width: 584px;
  height: 478px;
  border-radius: 24px;
  overflow: hidden;
  background-color: #fff;
  display: flex;
  flex-direction: column;
}

.project-img-wrapper {
  width: 584px;
  height: 378px;
}

.project-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.project-title-bar {
  width: 584px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #fff;
  font-weight: bold;
}

/* 5. Bottom Banner Section */
.bottom-banner-section {
  width: 100%;
  height: 773px;
  background-color: #fff;
  background-image: url("@/assets/product/kits/banner1.png"); /* 使用背景图 */
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bottom-banner-container {
  width: 1200px;
  height: 100%;
  display: flex;
  align-items: center;
  position: relative;
}

.bottom-banner-text {
  width: 500px;
  margin-left: auto; /* Push to right */
  margin-right: -120px; /* Adjust margin */
  font-size: 20px;
  color: #333;
  line-height: 1.8;
}
</style>
